import React, { useState, useEffect } from "react";
import fiveOffSvg from "../../assets/svg/landingPage/five-off.svg";
import closeSvg from "../../assets/svg/close.svg";
import { Modal } from "components/dashboard/Modal";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "../../store/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { OutlineButton } from "../buttons/OutlineButton";
import { PreSignupUser } from "../../store/actions/authActions/PreSignupUser";
import { InputField } from "components/InputField";

interface Props {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  downloadLink: string;
  promoCode?: string | null;
}

interface FormValues {
  email: string;
}

export const FiveDollarsOffPreSignupModal: React.FC<Props> = ({
  showModal,
  setShowModal,
  downloadLink,
  promoCode,
}) => {
  const { t } = useTranslation();
  const [preRegistrationSuccess, setPreRegistrationSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t("form.validEmail"))
      .max(100, t("form.emailTooLong"))
      .required(t("form.validEmail")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const handleFormSubmit = async (data: FormValues) => {
    const { email } = data;

    dispatch(
      PreSignupUser(
        email,
        promoCode || "SIGNUP_DISCOUNT_5",
        setPreRegistrationSuccess,
        setLoading
      )
    );
  };

  const handleDownload = () => {
    window.location.href = downloadLink;
    setShowModal(false);
  };

  useEffect(() => {
    if (preRegistrationSuccess) {
      localStorage.removeItem("promoCode");
      handleDownload();
    }
    // eslint-disable-next-line
  }, [preRegistrationSuccess]);

  return (
    <Modal
      customModal={
        <div className="text-slate-200 bg-slate-800 border-green-500 border-4 p-6 rounded-3xl shadow-2xl max-w-lg mx-auto relative">
          {/* Close Button */}
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-4 right-4 text-white"
          >
            <img className="h-6 w-6" src={closeSvg} alt="close" />
          </button>

          {/* Modal Header */}
          <div className="flex flex-col items-center space-y-4">
            <div className="flex space-x-4 items-center">
              <img className="h-20 w-20" src={fiveOffSvg} alt="5 dollars off" />
              <h1 className="text-2xl font-bold text-center">
                {t("banner.limitedTime")}
              </h1>
            </div>
            <p className="text-sm text-gray-300 text-center">
              {t("fiveOffModal.presignupInfo")}
            </p>
          </div>
          <div className="pt-6 space-y-6">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-4"
            >
              <InputField
                dark
                id="email"
                inputType="email"
                register={register("email")}
                label={t("form.email")}
                placeholder={t("form.emailPlaceHolder")}
              />
              <p className="text-red-500 text-xs">{errors.email?.message}</p>
              <div className="flex flex-col space-y-2">
                <OutlineButton
                  type="submit"
                  roundedSize="lg"
                  textSize="text-lg/[16px]"
                  outlineColor="green-500"
                  loading={loading}
                  hoverColor="slate-800"
                  hoverFont="semibold"
                >
                  {t("fiveOffModal.presignupSubmit")}
                </OutlineButton>
                <OutlineButton
                  type="button"
                  roundedSize="lg"
                  textSize="text-lg/[16px]"
                  outlineColor="gray-500"
                  onClick={handleDownload}
                >
                  {t("fiveOffModal.skip&Dowload")}
                </OutlineButton>
              </div>
            </form>
          </div>
        </div>
      }
      handleClose={() => setShowModal(false)}
      show={showModal}
    />
  );
};
